var __jsx = React.createElement;
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { MuscleButton } from "@modules/shared/components/buttons";
import Disclaimer from "@modules/shared/components/disclaimer";
import MuscleQuantityTogglerInput from "@modules/shared/components/quantity-toggler-input";
import { validatePassengersDetails } from "@lib/util/flights/input-validation-ops";
import InformativeCard from "@modules/shared/components/cards/informative";
import { useForwardNode } from "@modules/mobile/hooks/useForwardNode";
import { useSideOverPanel } from "@modules/mobile/hooks/useSideOverPanel";
import { useMobileFilters } from "@lib/context/mobile/filters-context";
import useCssModules from "@lib/hooks/use-css-modules";
const PassengerQuantitySelector = _ref => {
  let {
    children: childrenNode,
    onClose
  } = _ref;
  const t = useTranslations("Flights");
  const {
    setNode
  } = useForwardNode();
  const {
    togglePanel
  } = useSideOverPanel();
  const {
    cssModules
  } = useCssModules();
  const {
    passengerSelectionTitle
  } = (cssModules === null || cssModules === void 0 ? void 0 : cssModules.views.mobile.PassengerSelection) || {};
  const {
    flightFilters
  } = useMobileFilters();
  const allowBabyOnSeat = useMemo(() => {
    var _process$env$NEXT_PUB;
    return ((_process$env$NEXT_PUB = process.env.NEXT_PUBLIC_ALLOW_BABY_ON_SEAT) !== null && _process$env$NEXT_PUB !== void 0 ? _process$env$NEXT_PUB : "false") === "true";
  }, []);

  // Validation state
  const {
    0: errorMessage,
    1: setErrorMessage
  } = useState("");

  // Memoized validation result
  const validation = useMemo(() => validatePassengersDetails(flightFilters.passengersDetails), [flightFilters.passengersDetails]);

  // Update error state in context
  useEffect(() => {
    const hasError = !validation.validation.success;
    setErrorMessage(hasError ? t(validation.validation.message) : "");
    flightFilters.setHasPassengerError(hasError);
  }, [validation.validation.success, validation.validation.message, t, flightFilters]);

  // Memoized disabled states
  const disabledStates = useMemo(() => ({
    adults: {
      increment: !validation.validationValues.totalPassengerQtyValid,
      decrement: flightFilters.passengersDetails.adults === 0
    },
    children: {
      increment: !validation.validationValues.enoughAdultsForChildren || !validation.validationValues.totalPassengerQtyValid,
      decrement: flightFilters.passengersDetails.children === 0
    },
    infants: {
      increment: !validation.validationValues.enoughAdultsForInfants || !validation.validationValues.totalPassengerQtyValid,
      decrement: flightFilters.passengersDetails.infants === 0
    },
    infants_with_seat: {
      increment: !validation.validationValues.enoughAdultsForInfants || !validation.validationValues.totalPassengerQtyValid,
      decrement: flightFilters.passengersDetails.infants_with_seat === 0
    }
  }), [validation.validationValues, flightFilters.passengersDetails]);
  const handleHeaderClick = useCallback(() => {
    setNode("PassengerSelection");
    togglePanel();
  }, [setNode, togglePanel]);
  const handleReadyClick = useCallback(() => {
    if (!errorMessage) {
      onClose();
    }
  }, [errorMessage, onClose]);
  return __jsx("div", {
    className: "w-[275px] sm:w-[350px] flex flex-col p-4 gap-[6px]"
  }, __jsx("p", {
    className: passengerSelectionTitle,
    onClick: handleHeaderClick
  }, t("PASSENGERS")), __jsx(MuscleQuantityTogglerInput, {
    dataTestId: "flight-search-passengers-quantity-selector-adults-input",
    label: t("PASSENGERS_TYPE.ADULTS"),
    description: t("PASSENGERS_TYPE.ADULTS_DESCRIPTION"),
    value: flightFilters.passengersDetails.adults,
    onIncrement: () => flightFilters.incrementPassenger("adults"),
    onDecrement: () => flightFilters.decrementPassenger("adults"),
    incrementDisabled: disabledStates.adults.increment,
    decrementDisabled: disabledStates.adults.decrement
  }), __jsx(MuscleQuantityTogglerInput, {
    dataTestId: "flight-search-passengers-quantity-selector-children-input",
    label: t("PASSENGERS_TYPE.CHILDREN"),
    description: t("PASSENGERS_TYPE.CHILDREN_DESCRIPTION"),
    value: flightFilters.passengersDetails.children,
    onIncrement: () => flightFilters.incrementPassenger("children"),
    onDecrement: () => flightFilters.decrementPassenger("children"),
    incrementDisabled: disabledStates.children.increment,
    decrementDisabled: disabledStates.children.decrement
  }), __jsx(MuscleQuantityTogglerInput, {
    dataTestId: "flight-search-passengers-quantity-selector-infants-input",
    label: t("PASSENGERS_TYPE.BABIES_ON_LAP"),
    description: t("PASSENGERS_TYPE.BABIES_DESCRIPTION"),
    value: flightFilters.passengersDetails.infants,
    onIncrement: () => flightFilters.incrementPassenger("infants"),
    onDecrement: () => flightFilters.decrementPassenger("infants"),
    incrementDisabled: disabledStates.infants.increment,
    decrementDisabled: disabledStates.infants.decrement
  }), allowBabyOnSeat && __jsx(MuscleQuantityTogglerInput, {
    dataTestId: "flight-search-passengers-quantity-selector-infants-with-seat-input",
    label: t("PASSENGERS_TYPE.BABIES_WITH_SEAT"),
    description: t("PASSENGERS_TYPE.BABIES_DESCRIPTION"),
    value: flightFilters.passengersDetails.infants_with_seat,
    onIncrement: () => flightFilters.incrementPassenger("infants_with_seat"),
    onDecrement: () => flightFilters.decrementPassenger("infants_with_seat"),
    incrementDisabled: disabledStates.infants_with_seat.increment,
    decrementDisabled: disabledStates.infants_with_seat.decrement
  }), errorMessage && __jsx(InformativeCard, {
    message: errorMessage,
    severity: "danger"
  }), childrenNode !== null && childrenNode !== void 0 ? childrenNode : __jsx(Disclaimer, {
    text: t("DISCLAIMERS.PASSENGER_AGES_DISCLAIMER"),
    message: t("DISCLAIMERS.PASSENGER_AGES_DISCLAIMER_MESSAGE")
  }), __jsx(MuscleButton, {
    variant: "solidXL",
    disabled: !!errorMessage,
    onClick: handleReadyClick,
    dataTestId: "flight-search-passengers-quantity-selector-ready-button"
  }, t("READY")));
};
export default PassengerQuantitySelector;